import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { DataTable, DataTableBody, DataTableCell, DataTableRow } from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tKgpco/theme/design";
import { typography } from "@tKgpco/theme/styleguide";
import React, { FC, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    product: any;
}

type Props = OwnProps;

export interface SeeMoreInfoModalStyles {
    container?: GridContainerProps;
    seeMoreInfoGridItem?: GridItemProps;
    infoGridItem?: GridItemProps;
    seeMoreInfoLink?: LinkPresentationProps;
    seeMoreInfoModal?: ModalPresentationProps;
    infoLabelText?: TypographyPresentationProps;
    infoValueText?: TypographyPresentationProps;
}

export const seeMoreInfoModalStyles: SeeMoreInfoModalStyles = {
    container: {
        gap: 5,
        css: css`
            margin: 15px;
        `,
    },
    seeMoreInfoLink: {
        css: css`
            font-size: 14px;
        `,
    },
    seeMoreInfoGridItem: {
        width: 12,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        justify-content: flex-end;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    infoLabelText: {
        weight: "bold",
        css: css`
            flex: 0 0 auto;
        `,
    },
    infoValueText: {
        css: css`
            flex: 1 1 auto;
            min-width: 0;
            ${wrapInContainerStyles}
        `,
    },
};
const styles = seeMoreInfoModalStyles;

const SeeMoreInfoModal: FC<Props> = ({ product }) => {
    const { detail } = product;
    const [modelOpen, setModelOpen] = useState<boolean>(false);
    const openSeeMoreInfoModal = () => {
        setModelOpen(true);
    };

    const modalCloseHandler = () => {
        setModelOpen(false);
    };

    const shippingLength = detail?.shippingLength
        ? detail?.shippingLength
        : (product?.properties?.shippingLength ?? "");
    const shippingHeight = detail?.shippingHeight
        ? detail?.shippingHeight
        : (product?.properties?.shippingHeight ?? "");
    const shippingWidth = detail?.shippingWidth ? detail?.shippingWidth : (product?.properties?.shippingWidth ?? "");
    const shippingWeight = detail?.shippingWeight
        ? detail?.shippingWeight
        : (product?.properties?.shippingWeight ?? "");
    const isHazardousGood = detail?.isHazardousGood
        ? detail?.isHazardousGood
        : (product?.properties?.isHazardousGood ?? "");
    return (
        <StyledWrapper>
            <GridItem {...styles.seeMoreInfoGridItem}>
                <StyledWrapper>
                    <Link {...styles.seeMoreInfoLink} data-test-selector="seeMoreInfo" onClick={openSeeMoreInfoModal}>
                        {translate("See More Info")}
                    </Link>
                </StyledWrapper>
            </GridItem>
            <Modal
                {...styles.seeMoreInfoModal}
                headline={translate("More information about product")}
                isOpen={modelOpen}
                handleClose={modalCloseHandler}
                sizeVariant="small"
            >
                <GridContainer {...styles.container}>
                    <DataTable>
                        <DataTableBody>
                            <DataTableRow>
                                <DataTableCell>{siteMessage("ShippingLength")}</DataTableCell>
                                <DataTableCell>{shippingLength ? `${shippingLength}` : "NA"}</DataTableCell>
                            </DataTableRow>
                            <DataTableRow>
                                <DataTableCell>{siteMessage("ShippingHeight")}</DataTableCell>
                                <DataTableCell>{shippingHeight ? `${shippingHeight}` : "NA"}</DataTableCell>
                            </DataTableRow>
                            <DataTableRow>
                                <DataTableCell>{siteMessage("ShippingWidth")}</DataTableCell>
                                <DataTableCell>{shippingWidth ? `${shippingWidth}` : "NA"}</DataTableCell>
                            </DataTableRow>
                            <DataTableRow>
                                <DataTableCell>{siteMessage("ShippingWeight")}</DataTableCell>
                                <DataTableCell>{shippingWeight ? `${shippingWeight}` : "NA"}</DataTableCell>
                            </DataTableRow>
                            <DataTableRow>
                                <DataTableCell>{siteMessage("isOverlength")}</DataTableCell>
                                <DataTableCell>
                                    {product?.properties?.isOverlength
                                        ? `${product.properties.isOverlength?.toLowerCase() === "true" ? "Yes" : "No"}`
                                        : "NA"}
                                </DataTableCell>
                            </DataTableRow>
                            <DataTableRow>
                                <DataTableCell>{siteMessage("IsHazardousGood")}</DataTableCell>
                                <DataTableCell>
                                    {isHazardousGood
                                        ? `${isHazardousGood?.toString().toLowerCase() === "true" ? "Yes" : "NA"}`
                                        : "NA"}
                                </DataTableCell>
                            </DataTableRow>
                        </DataTableBody>
                    </DataTable>
                </GridContainer>
            </Modal>
        </StyledWrapper>
    );
};

export default SeeMoreInfoModal;
